/* import __COLOCATED_TEMPLATE__ from './connect.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import type { ConnectionOption } from 'embercom/components/standalone/common/connect-card';
import type SalesforceConfiguration from 'embercom/models/standalone/salesforce-configuration';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type IntlService from 'embercom/services/intl';
import type ImportSource from 'embercom/models/import-service/import-source';

interface Args {
  nextStep: () => void;
  previousStep: () => void;
  sourceId?: number;
  importSource?: ImportSource;

  setImportSource?: (importSource: ImportSource) => void;
  toggleSideDrawer: () => void;
}

export default class StandaloneSalesforceArticleSyncStepConnect extends Component<Args> {
  @service declare finStandaloneService: FinStandaloneService;
  @service declare intl: IntlService;
  @service declare appService: AppService;
  @service declare redirectService: any;

  @tracked showManageConnection = false;
  @tracked importSource: ImportSource | undefined;
  @tracked selectedDomain: string | undefined;

  constructor(owner: any, args: Args) {
    super(owner, args);
    if (!this.isStandaloneApp) {
      this.showManageConnection = true;
    }
    // Use the import source passed from step-wrapper if available
    if (this.args.importSource) {
      this.importSource = this.args.importSource;
    }
  }

  get salesforceConfig(): SalesforceConfiguration | undefined {
    return this.finStandaloneService.salesforceConfig;
  }

  get salesforceConnectionCard(): ConnectionOption {
    return {
      value: 'salesforce',
      icon: 'salesforce',
      label: this.intl.t(
        'standalone.salesforce.setup.article-sync.step-wrapper.steps.connect.label',
      ),
      description: this.intl.t(
        'standalone.salesforce.setup.article-sync.steps.connect.description',
      ),
      isConnected: (this.salesforceConfig?.isConnected || this.importSource !== undefined) ?? false,
      isAuthFailed: this.salesforceConfig?.isAuthFailed ?? false,
      stampConnected: this.intl.t(
        'standalone.salesforce.setup.article-sync.steps.connect.stamp-connected',
        {
          environmentLabel: this.environmentLabel ?? '',
        },
      ),
      manage: () => {
        this.toggleManageConnection();
      },
      connect: () => {
        this.toggleManageConnection();
      },
    };
  }

  get continueButtonLabel() {
    if (this.salesforceConfig?.isConnected) {
      return this.intl.t(
        'standalone.salesforce.setup.article-sync.steps.connect.continue-with-button-label',
        {
          environmentLabel: this.environmentLabel ?? '',
        },
      );
    } else {
      return this.intl.t(
        'standalone.salesforce.setup.article-sync.steps.connect.continue-button-label',
      );
    }
  }

  get isStandaloneApp() {
    return this.appService.app.isStandaloneApp;
  }

  get environmentLabel() {
    if (this.salesforceConfig?.environmentLabel !== undefined) {
      return this.salesforceConfig?.environmentLabel;
    } else if (this.selectedDomain !== undefined) {
      return this.selectedDomain;
    }
    return '';
  }

  @action startImportOnlyConnectFlow() {
    if (this.finStandaloneService.salesforceImportOnlyConfig) {
      this.args.nextStep();
    } else {
      let { protocol, host } = window.location;
      let uri = `${protocol}//${host}/standalone/salesforce/connect`;
      let params = `app_id_code=${this.appService.app.id}&sandbox=false&import_only=true&connect_redirect_location=article_sync`;
      this.redirectService.redirect(`${uri}?${params}`);
    }
  }

  @action toggleManageConnection() {
    if (!this.isStandaloneApp) {
      this.args.toggleSideDrawer();
    }
    this.showManageConnection = !this.showManageConnection;
  }

  @action handleImportSourceCreated(importSource: ImportSource, selectedDomain: string) {
    this.importSource = importSource;
    this.selectedDomain = selectedDomain;
    // Lift import source back to the step-wrapper.
    if (this.args.setImportSource) {
      this.args.setImportSource(importSource);
    }

    this.args.nextStep();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Salesforce::Setup::ArticleSync::Steps::Connect': typeof StandaloneSalesforceArticleSyncStepConnect;
    'standalone/salesforce/setup/article-sync/steps/connect': typeof StandaloneSalesforceArticleSyncStepConnect;
  }
}
